// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import {Component, HostListener, OnInit, ViewChild} from '@angular/core';
import {BaseWidget} from 'gridstack/dist/angular';
import { icon, latLng, marker, tileLayer, MarkerClusterGroupOptions, Layer, LatLngBounds, MapOptions } from 'leaflet';
import {D3eService} from '../../../../services/d3e-service';
import {HeaderService} from '../../../header.service';
import { catchError, map, switchMap } from 'rxjs/operators';
import {BehaviorSubject, combineLatest, forkJoin, Observable, of} from 'rxjs';
import {ConfigurationService} from '../../../../services/configuration-service';
import {AuthService} from '../../../../services/auth-service';
import { LeafletDirective } from '@bluehalo/ngx-leaflet';
import {Router} from '@angular/router';
import {DashboardGridService} from '../dashboard-grid.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {EventsService} from '../../../../services/events-service';
import {EVENT_TYPES} from '../../../../types/events';
import { SelectedOrgMethod } from '../../../../shared/selectedOrg';
import { EndpointFilter } from '../../dashboard/dashboard.types';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-endpoint-location',
    templateUrl: './endpoint-location.component.html',
    styleUrls: ['./endpoint-location.component.scss', '../dynamic-dashboard.common.scss'],
    standalone: false
})
export class EndpointLocationComponent extends BaseWidget implements OnInit {
  @ViewChild(LeafletDirective) leafletDirective: LeafletDirective;
  filterValue$: BehaviorSubject<EndpointFilter> = new BehaviorSubject<EndpointFilter>('two_weeks');
  mapOptions$: Observable<MapOptions> = of({});
  markerClusterData$: Observable<Layer[]> = of([]);

  markerClusterOptions: MarkerClusterGroupOptions = {
    showCoverageOnHover: false
  };

  constructor(private readonly d3eService: D3eService,
              private readonly authService: AuthService,
              private readonly configurationService: ConfigurationService,
              private readonly router: Router,
              private readonly eventsService: EventsService,
              private readonly dashboardGridService: DashboardGridService,
              private readonly headerService: HeaderService) {
    super();
  }

  ngOnInit() {
    this.markerClusterData$ = combineLatest([
      this.headerService.orgSelectedChanged,
      this.filterValue$,
    ]).pipe(
      switchMap(([selectedOrgId, filterValue]) => {
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(selectedOrgId);
        return forkJoin({
          endpointsResponse: this.d3eService.get_endpoints_location({'filter': filterValue}, selectedOrg),
          eventResponse: this.eventsService.debug({
            type: EVENT_TYPES.MAP_LOAD,
            description: "Loaded a map.",
            detail: `Loaded map of endpoints for ${selectedOrg}`,
          }).pipe(catchError(() => of(null)))
        });
      }),
      map(({endpointsResponse, eventResponse}) => {
        const bounds = new LatLngBounds(endpointsResponse.endpoint_locations.map(e => [e.lat, e.lon]));
        this.leafletDirective.map.invalidateSize();
        try {
          this.leafletDirective.map.fitBounds(bounds);
        } catch (e) {
          // failed to auto-zoom the map, this can happen if there are no endpoints that can be mapped
          this.leafletDirective.map.setZoom(2);
          this.leafletDirective.map.panTo(latLng(0, 0));
        }

        const data: Layer[] = endpointsResponse.endpoint_locations.map(endpoint => marker([endpoint.lat, endpoint.lon], {
          title: (endpoint.computer_name || "Computer") +
            (endpoint.username ? ` (${endpoint.username})` : ""),
          icon: icon({
            iconUrl: this.configurationService.mapWidgetConfig.icon,
            iconSize: [this.configurationService.mapWidgetConfig.width, this.configurationService.mapWidgetConfig.height]
          }),
        }).bindPopup(layer => `
                <p><a href="#" data-uuid="${endpoint.uuid}">${endpoint.computer_name}</a></p>
                <p>${endpoint.username || 'Never Logged In'}</p>
                <p>v${endpoint.version}</p>
                <p>Last Connected: ${new Date(endpoint.last_check_dttm).toLocaleString()}</p>
                <p>${endpoint.org_name || ''}</p>
              `
        ));
        return data;
      }),
    );

    this.mapOptions$ = this.authService.mapToken$.pipe(
      map(token => ({
        attributionControl: false,
        layers: [
          tileLayer(this.configurationService.mapTilesUrl + "?token=Bearer%20" + token, {
            maxZoom: 15,
            attribution: '',
          })
        ],
        minZoom: 1,
        zoom: 2,
        worldCopyJump: true,
        center: latLng(0, 0)
      }))
    );

    this.dashboardGridService.gridResize
      .pipe(untilDestroyed(this))
      .subscribe(() => this.leafletDirective?.map?.invalidateSize());
  }

  @HostListener('click', ['$event'])
  onMarkerClick(event: PointerEvent) {
    const element = event.target as HTMLElement;
    const uuid = element.getAttribute('data-uuid');
    if (!!uuid) {
      this.router.navigate(['device', uuid]);
    }
    return false;
  }

}
